import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  DialogTitle,
  Button,
  DialogContent,
  Dialog,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StarIcon from "@mui/icons-material/Star";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import SendOutlined from "@mui/icons-material/SendOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import CustomeDataGrid from "../../components/common/CustomeDataGrid";
import apiService, { BaseURL } from "../../service/Api";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/Index";
import { logout } from "../../store/slice/AuthSlice";
import { useNavigate } from "react-router-dom";
import type {
  Booking,
  FrontendStatus,
  PaymentStatus,
} from "../../types/bookings";
import type {
  BookingResponse,
  BackendStatus,
} from "../bookings/types/list-bookings";
import StatCard from "../client-portal-admin/components/StatsCard";
import FilterTabs from "../client-portal-admin/components/FilterTabs";
import formatDate from "../../utils/FormatDate";
import { tokens } from "../../theme";

// Status mappings from ListBookings
const statusMapToFrontend: Record<BackendStatus, FrontendStatus> = {
  pending: "enquiry",
  confirmed: "confirmed",
  completed: "fulfilled",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

const statusMapToBackend: Record<FrontendStatus, BackendStatus> = {
  enquiry: "pending",
  confirmed: "confirmed",
  fulfilled: "completed",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

const getClientPortalBase = (): string => {
  if (BaseURL.includes("mms.muzukuru.com")) return "https://my.muzukuru.com";
  return "https://client.muzukuru.com";
};

const CLIENT_PORTAL_BASE = getClientPortalBase();

const ClientMemorialDashboard: React.FC = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<number | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check permissions
  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  // Fetch bookings data
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const apiPage = paginationModel.page + 1;
      const params: Record<string, string | number | boolean> = {
        page: apiPage,
        page_size: paginationModel.pageSize,
        source_of_booking: "client_portal",
        search: appliedSearchTerm,
        ordering: "-id",
      };

      const response = await apiService.get<{
        count: number;
        results: BookingResponse[];
      }>("/api/bookings/booking/", params);

      const fetchedBookings: Booking[] = response.data.results
        .filter((booking): booking is BookingResponse => {
          if (!booking || typeof booking !== "object" || !booking.id) {
            console.warn("Invalid booking data filtered out:", booking);
            return false;
          }
          return true;
        })
        .map((booking) => {
          let computedTitle: string;
          let deceasedIdFromBooking: number | null = null;
          let deceasedSlug: string | undefined = undefined;

          if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CHURCH" &&
            booking.services &&
            booking.services.length > 0 &&
            booking.services[0].church_name
          ) {
            computedTitle = booking.events[0]?.title || "";
          } else if (
            booking.booking_type &&
            booking.booking_type.toUpperCase() === "CONFERENCE" &&
            booking.events.length > 0
          ) {
            computedTitle = booking.events[0]?.title || "";
          } else if (booking.deceased && booking.deceased.length > 0) {
            const firstDeceased = booking.deceased[0] as {
              id?: number;
              first_name?: string;
              surname?: string;
              [key: string]: unknown;
            };
            const deceasedName = firstDeceased.first_name || "";
            const deceasedSurname = firstDeceased.surname || "";
            computedTitle =
              `${deceasedName} ${deceasedSurname}`.trim() ||
              "Deceased Name N/A";
            deceasedIdFromBooking =
              (firstDeceased.id as number | undefined) ?? null;
            deceasedSlug = `${deceasedName} ${deceasedSurname}`
              .trim()
              .toLowerCase()
              .replace(/\s+/g, "-");
          } else if (
            booking.client &&
            (booking.client.salutation || booking.client.company_name)
          ) {
            computedTitle = `${booking.client.salutation || ""} ${
              booking.client.company_name || ""
            }`.trim();
          } else if (booking.title) {
            computedTitle = booking.title;
          } else {
            computedTitle = "Booking N/A";
          }

          const service =
            booking.service && booking.service.length < 50
              ? booking.service
              : booking.booking_type && booking.booking_type.length < 50
                ? booking.booking_type
                : "N/A";

          const packageName =
            booking.package_item?.name || "No selected package";
          const paymentStatus: PaymentStatus =
            booking.payment_status?.toLowerCase() === "paid"
              ? "paid"
              : booking.payment_status?.toLowerCase() === "sponsored"
                ? "sponsored"
                : booking.payment_status?.toLowerCase() === "other"
                  ? "other"
                  : "pending";

          const startTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].start_time || "00:00:00"
              : "00:00:00";

          const endTime =
            booking.services && booking.services.length > 0
              ? booking.services[0].end_time || "23:59:59"
              : "23:59:59";

          const validStatuses = [
            "pending",
            "confirmed",
            "completed",
            "cancelled",
            "lost",
            "testing",
          ];

          const status: FrontendStatus = validStatuses.includes(booking.status)
            ? statusMapToFrontend[booking.status as BackendStatus] || "enquiry"
            : "enquiry";

          const bookingType =
            booking.booking_type && booking.booking_type.length < 50
              ? booking.booking_type
              : "N/A";
          const dateOfService = formatDate(
            booking?.services[0]?.date_of_service ||
              `${booking?.events[0]?.start_date_of_event || ""} -${
                booking?.events[0]?.end_date_of_event || ""
              }` ||
              "No date of service",
          );

          return {
            id: booking.id,
            title: computedTitle,
            service,
            package: packageName,
            paymentStatus,
            dateOfService,
            status,
            bookingType,
            startTime,
            endTime,
            deceasedId: deceasedIdFromBooking ?? undefined,
            deceasedSlug,
          };
        });

      setBookings(fetchedBookings);
      setRowCount(response.data.count);
      setError(null);
    } catch (err: unknown) {
      let errorMessage = "Failed to load bookings. Please try again.";
      const errorResponse = (
        err as {
          response?: { status?: number; data?: { detail?: string } };
        }
      )?.response;
      if (errorResponse?.status === 500) {
        errorMessage = "Server error: Unable to retrieve bookings.";
      } else if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Failed to load bookings:", err);
      const errorInfo = err as {
        message?: string;
        response?: { data?: unknown };
        stack?: string;
      };
      console.error("Fetch bookings error:", {
        message: errorInfo.message,
        response: errorInfo.response?.data,
        stack: errorInfo.stack,
      });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearchTerm, dispatch, navigate, paginationModel]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (searchValue !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(searchValue);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchValue, appliedSearchTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalMemorials = bookings.length;
    const freeMemorials = bookings.filter(
      (item) => item.package === "No selected package",
    ).length;
    const premiumMemorials = bookings.filter(
      (item) => item.package !== "No selected package",
    ).length;
    const totalBookings = bookings.length;

    return {
      totalMemorials,
      freeMemorials,
      premiumMemorials,
      totalBookings,
    };
  }, [bookings]);

  // Filter data based on active tab and search
  const filteredData = useMemo(() => {
    let filtered = bookings;

    // Filter by tab
    if (activeTab === 1) {
      filtered = filtered.filter(
        (item) => item.package !== "No selected package",
      );
    } else if (activeTab === 2) {
      filtered = filtered.filter(
        (item) => item.package === "No selected package",
      );
    }

    // Filter by search
    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.service.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.package.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }

    return filtered;
  }, [activeTab, searchValue, bookings]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleStatusChange = async (id: number, newStatus: FrontendStatus) => {
    try {
      const backendStatus = statusMapToBackend[newStatus];
      await apiService.patch(`/api/bookings/booking/${id}/`, {
        status: backendStatus,
      });
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === id ? { ...booking, status: newStatus } : booking,
        ),
      );
      toast.success("Status updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: unknown) {
      setError("Failed to update status");
      toast.error("Failed to update status", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Status update error:", err);
    }
  };

  const confirmDelete = (id: number) => {
    setBookingToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (bookingToDelete === null) return;
    try {
      await apiService.delete(`/api/bookings/booking/${bookingToDelete}/`);
      setBookings((prevBookings) =>
        prevBookings.filter((booking) => booking.id !== bookingToDelete),
      );
      toast.success("Booking deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
      fetchBookings();
    } catch (err: unknown) {
      setError("Failed to delete booking");
      toast.error("Failed to delete booking", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Delete booking error:", err);
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setBookingToDelete(null);
  };

  const columns: GridColDef<Booking>[] = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
    },
    {
      field: "package",
      headerName: "Package",
      flex: 1,
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <Select
            value={params.row?.status || "enquiry"}
            onChange={(event) =>
              params.row?.id &&
              handleStatusChange(
                params.row.id,
                event.target.value as FrontendStatus,
              )
            }
            onClick={(event) => event.stopPropagation()}
            onOpen={(event) => event.stopPropagation()}
            MenuProps={{
              onClick: (event) => event.stopPropagation(),
            }}
            sx={{
              "& .MuiSelect-select": {
                padding: "4px 8px",
                width: "100%",
                color: colors.neutral[100],
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="enquiry">
              <Chip
                label="Enquiry"
                sx={{
                  bgcolor: "#F79E1B40",
                  color: "#F79E1B",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="confirmed">
              <Chip
                label="Confirmed"
                sx={{
                  bgcolor: "#4BA66340",
                  color: "#4BA663",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="fulfilled">
              <Chip
                label="Fulfilled"
                sx={{
                  bgcolor: "#0000FF26",
                  color: "#0000FF",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="cancelled">
              <Chip
                label="Cancelled"
                sx={{
                  bgcolor: "#EB433540",
                  color: "#EB4335",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {params.row.deceasedId && params.row.deceasedSlug && (
            <Tooltip title="View Memorial Page" arrow>
              <IconButton
                aria-label="view memorial page"
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(
                    `${CLIENT_PORTAL_BASE}/memorial-page/${params.row.deceasedId}/${params.row.deceasedSlug}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
                sx={{
                  color: colors.primary[400],
                  "&:hover": { backgroundColor: colors.neutral[800] },
                }}
              >
                <OpenInNewOutlined />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === "enquiry" && (
            <Tooltip title="Send Proposal" arrow>
              <IconButton
                aria-label="send proposal"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate("/proposals/proposals-builder", {
                    state: {
                      bookingId: params.row.id,
                      deceasedId: params.row.deceasedId,
                    },
                  });
                }}
                sx={{
                  color: colors.primary[400],
                  "&:hover": {
                    backgroundColor: colors.neutral[800],
                  },
                }}
              >
                <SendOutlined />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission("booking.change_booking") && (
            <IconButton
              aria-label="edit"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/bookings/${params.row.id}/edit`);
              }}
              sx={{
                color: colors.primary[400],
                "&:hover": {
                  backgroundColor: colors.neutral[800],
                },
              }}
            >
              <EditOutlined />
            </IconButton>
          )}
          {hasPermission("booking.delete_booking") && (
            <IconButton
              aria-label="delete"
              onClick={(event) => {
                event.stopPropagation();
                confirmDelete(params.row.id);
              }}
              sx={{
                color: "#EB4335",
                "&:hover": {
                  backgroundColor: colors.neutral[800],
                },
              }}
            >
              <DeleteOutline />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleRowClick = (params: GridRowParams<Booking>) => {
    navigate(`/memorial-pages-dashboard/${params.row.id}`);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" my={4}>
        <Typography color="error">{error}</Typography>
        <Button
          onClick={() => {
            setError(null);
            setPaginationModel({ page: 0, pageSize: 10 });
            fetchBookings();
          }}
          variant="contained"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Memorials"
            value={stats.totalMemorials}
            icon={<PeopleIcon />}
            iconColor="#4caf50"
            iconBgColor="#e8f5e8"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Free Memorials"
            value={stats.freeMemorials}
            icon={<AccessTimeIcon />}
            iconColor="#2196f3"
            iconBgColor="#e3f2fd"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Premium Memorials"
            value={stats.premiumMemorials}
            icon={<StarIcon />}
            iconColor="#9c27b0"
            iconBgColor="#f3e5f5"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Bookings"
            value={stats.totalBookings}
            icon={<BookOnlineIcon />}
            iconColor="#ff9800"
            iconBgColor="#fff3e0"
          />
        </Grid>
      </Grid>

      {/* Memorial Pages Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Memorial Pages
        </Typography>

        <FilterTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
        />

        <CustomeDataGrid
          rows={filteredData}
          columns={columns}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={handleRowClick}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[500],
              color: colors.primary[100],
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: colors.primary[500],
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: colors.primary[100],
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
              justifyContent: "center",
              padding: 1,
            },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ color: colors.neutral[100] }}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} sx={{ color: "#EB4335" }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientMemorialDashboard;
